// Messages' screens (PMU-233, PMU-234), shared by the parent app, the staff app and the console:
// the inbox, a conversation (files, translation, read receipts, a class channel's setting, adults a
// parent adds, the office's flags and export), settings (language, office hours), and the
// console's safeguarding page. Live: a new message arrives on the site's live socket the app
// already has open (onModuleLive); a slow poll covers phones without one.
import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Avatar, Badge, Banner, Button, Checkbox, Dialog, DialogClose, EmptyState, Icon, IconButton, Menu, Select, Spinner, Switch, TextField, type MenuItem } from "@vavr/ui";
import { api, blobData, onModuleLive, ShellHeader, slot, useBlobUrl, useOffline, useShellSlotsHere, type ModuleLiveMessage, type TabCount } from "@vavr/screens/shared";
import { ReportDialog, RulesCard, useTerms } from "../safety/screens.tsx";
import "./messages.css";

export type As = "parent" | "family" | "teacher" | "office" | "staff" | "dispatch" | "system";
export type Kind = "family" | "staff" | "class" | "school" | "partner" | "bus";
export interface InboxItem {
  thread_id: string | null; kind: Kind; as: As; site_id: string | null; child_id: string | null; org_id: string;
  title: string; subtitle: string; last_at: number | null; last_text: string | null; last_by: string | null; unread: number;
}
export interface Inbox { me: string; threads: InboxItem[]; unread: number; staff_orgs: { id: string; name: string }[] }
interface Message { id: string; thread_id: string; author_id: string; author_name: string; author_as: As; body: string; created_at: number; request_kind?: string | null; flagged?: string | null; reviewed?: boolean; hidden?: boolean; blocked?: boolean }
interface MsgFile { id: string; message_id: string; name: string; type: string; size: number }
interface ThreadView {
  thread: { id: string; kind: Kind; as: As; title: string; subtitle: string; can_post: boolean; read_only_why: string | null; site_id: string | null; child_id: string | null; org_id: string; replies: boolean; can_settings: boolean; can_export: boolean; can_add_family: boolean;
    can_moderate?: boolean; me_staff?: boolean; dm_with?: string | null };
  me: string; messages: Message[]; files: MsgFile[]; more: boolean;
  /** The furthest anyone else has received and read: the ticks on your own messages. */
  ticks?: { delivered_at: number; read_at: number };
  receipts: { people?: { name: string; read_at: number }[]; parents?: { total: number; read_at: number[] } };
}
interface Prefs { lang: string | null; quiet_start: string | null; quiet_end: string | null; tz: string | null; away_text: string | null; quiet_now: boolean; translate: { available: boolean; languages: { code: string; name: string }[] } }

/** Parents see only the conversations they take part in as a parent (not a staff account's). */
const PARENT_SIDE = new Set<As>(["parent", "family"]);

// ---------- the unread count on the tab, kept for the whole app ----------

let unread = 0;
let inbox: Inbox | null = null;
let inboxError: string | null = null;
let parentOnly = false;
const subs = new Set<() => void>();
const changed = () => subs.forEach((f) => f());
const countOf = (b: Inbox) => b.threads.filter((t) => !parentOnly || PARENT_SIDE.has(t.as)).reduce((s, t) => s + t.unread, 0);
export const unreadCount: TabCount = { get: () => unread, subscribe: (fn) => { subs.add(fn); return () => { subs.delete(fn); }; } };

let loading: Promise<void> | null = null;
let again = false;
/**
 * Reloads the inbox (and the count). A call made while one is running asks for one more after it:
 * the running one may have started before a conversation was marked read, and would show it unread.
 */
export function refreshInbox(): Promise<void> {
  if (loading) { again = true; return loading; }
  loading = api<Inbox>("GET", "/api/v1/messages").then((r) => { inbox = r; inboxError = null; unread = countOf(r); changed(); }, (e: Error) => { inboxError = e.message; changed(); })
    .finally(() => { loading = null; if (again) { again = false; void refreshInbox(); } });
  return loading;
}
function useInbox() {
  const [, bump] = useState(0);
  useEffect(() => { const f = () => bump((x) => x + 1); subs.add(f); void refreshInbox(); return () => { subs.delete(f); }; }, []);
  return { data: inbox, loadError: inboxError };
}

let prefsCache: Prefs | null = null;
function usePrefs(): [Prefs | null, () => void] {
  const [p, setP] = useState<Prefs | null>(prefsCache);
  const load = useMemo(() => () => { void api<Prefs>("GET", "/api/v1/messages/prefs").then((r) => { prefsCache = r; setP(r); }, () => undefined); }, []);
  useEffect(() => { load(); }, [load]);
  return [p, load];
}

/** Wraps an app: keeps the count current (on open, on a new message, when the app comes back, every 2 min). `parents`: the parent app. */
export function MessagesWatch({ children, parents }: { children: ReactNode; parents?: boolean }) {
  parentOnly = !!parents;
  useEffect(() => {
    void refreshInbox();
    // A receipt (someone received or read your message) changes no count.
    const off = onModuleLive((m) => { if (m.module === "messages" && m.type !== "receipt") void refreshInbox(); });
    const wake = () => { if (document.visibilityState === "visible") void refreshInbox(); };
    document.addEventListener("visibilitychange", wake);
    const t = setInterval(wake, 120_000);
    return () => { off(); document.removeEventListener("visibilitychange", wake); clearInterval(t); };
  }, []);
  return <>{children}</>;
}

// ---------- the inbox ----------

const ROLE: Partial<Record<As, string>> = { parent: "Parent", family: "Family", teacher: "Teacher", office: "Office", dispatch: "Bus company" };
const SECTIONS: { id: string; title: (parent: boolean) => string; kinds: Kind[] }[] = [
  { id: "news", title: () => "Announcements", kinds: ["school", "class"] },
  { id: "family", title: (parent) => (parent ? "Your children's classes" : "Families"), kinds: ["family"] },
  { id: "bus", title: () => "Buses", kinds: ["bus", "partner"] },
  { id: "staff", title: () => "Staff", kinds: ["staff"] },
];

function when(ts: number, tz?: string) {
  const d = new Date(ts), now = new Date();
  const opts: Intl.DateTimeFormatOptions = d.toDateString() === now.toDateString() ? { hour: "numeric", minute: "2-digit" }
    : now.getTime() - ts < 6 * 86_400_000 ? { weekday: "short" } : { month: "short", day: "numeric" };
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(d);
}
const ICON: Partial<Record<Kind, "bell" | "bus" | "message" | "user">> = { school: "bell", class: "bell", bus: "bus", partner: "bus" };

/**
 * The inbox. `open(id)` shows a conversation. `startFamily`: the school this member of staff may
 * start a family conversation at. `parents`: the parent app, which shows only a parent's own side.
 */
export function InboxView({ open, startFamily, tz, compact, parents, staffHours, settings, selectedId }: { open: (id: string) => void; startFamily?: string | null; tz?: string; compact?: boolean; parents?: boolean; staffHours?: boolean; settings?: boolean; selectedId?: string | null }) {
  const { data, loadError } = useInbox();
  const [query, setQuery] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  useEffect(() => {
    if (!compact) return;
    return onModuleLive((m) => { if (m.module === "messages" && m.type !== "receipt") void refreshInbox(); });
  }, [compact]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!data) return loadError ? <Banner tone="danger" title="Messages could not be loaded" actions={<Button size="sm" onClick={() => void refreshInbox()}>Try again</Button>}>{loadError}</Banner> : <div className="pmu-center"><Spinner label="Loading messages" /></div>;
  const startWith = async (item: InboxItem) => {
    if (item.thread_id) { open(item.thread_id); return; }
    const key = `${item.kind}:${item.child_id}:${item.org_id}`;
    setBusy(key); setError(null);
    try {
      const r = item.kind === "bus"
        ? await api<{ id: string }>("POST", "/api/v1/messages/bus", { site_id: item.site_id, child_id: item.child_id, org_id: item.org_id })
        : await api<{ id: string }>("POST", "/api/v1/messages/family", { site_id: item.site_id, child_id: item.child_id });
      void refreshInbox(); open(r.id);
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  const threads = parents ? data.threads.filter((t) => PARENT_SIDE.has(t.as)) : data.threads;
  const search = query.trim().toLowerCase();
  const shown = compact ? threads.filter((t) => (!onlyUnread || t.unread > 0) && (!search || `${t.title} ${t.subtitle} ${t.last_text ?? ""} ${t.last_by ?? ""}`.toLowerCase().includes(search))) : threads;
  const Threads = compact ? "div" : Fragment;
  const unreadTotal = threads.reduce((sum, t) => sum + t.unread, 0);
  const row = (t: InboxItem) => {
    const key = t.thread_id ?? `${t.kind}:${t.child_id}:${t.org_id}`;
    const icon = ICON[t.kind];
    return (
      <li key={key}>
        <button type="button" className="pmm-row" aria-current={selectedId && selectedId === t.thread_id ? true : undefined} data-unread={t.unread > 0 || undefined} onClick={() => void startWith(t)} disabled={busy === key}>
          {icon ? <span className="pmm-row__icon" aria-hidden="true"><Icon name={icon} /></span>
            : <Avatar name={t.kind === "family" && PARENT_SIDE.has(t.as) ? t.title.replace(/'s class$/, "") : t.title} kind={t.kind === "family" ? "child" : "adult"} size="md" />}
          <span className="pmm-row__main">
            <span className="pmm-row__top"><b className="pmm-row__title">{t.title}</b>{t.last_at ? <span className="pmm-row__when">{when(t.last_at, tz)}</span> : null}</span>
            {compact && <span className="pmm-row__context">{t.subtitle}</span>}
            <span className="pmm-row__sub">{t.last_text ? `${t.last_by ? `${t.last_by}: ` : ""}${t.last_text}` : t.thread_id ? t.subtitle : `Start a conversation · ${t.subtitle}`}</span>
          </span>
          {t.unread > 0 && <span className="pmm-row__count" aria-label={`${t.unread} unread`}>{t.unread}</span>}
        </button>
      </li>
    );
  };
  const canStaff = !parents && data.staff_orgs.length > 0;
  const isParent = threads.some((t) => PARENT_SIDE.has(t.as));
  return (
    <div className={`pmm-inbox${compact ? " pmm-inbox--compact" : ""}`}>
      {compact && <header className="pmm-inbox__summary"><div><h2>Inbox</h2><p>{threads.length} {threads.length === 1 ? "conversation" : "conversations"} · {unreadTotal} unread {unreadTotal === 1 ? "message" : "messages"}</p></div><Button size="sm" variant="ghost" onClick={() => void refreshInbox()}>Refresh</Button></header>}
      {loadError && <Banner tone="warning" title="Inbox refresh failed" actions={<Button size="sm" onClick={() => void refreshInbox()}>Try again</Button>}>{loadError}</Banner>}
      {error && <Banner tone="danger" title={error} />}
      {(settings || (startFamily && !parents) || canStaff) && (
        <div className="pmm-inbox__actions">
          {/* In the apps the settings are in the top bar (MessagesTopBar); the console keeps them here. */}
          {settings && <PrefsButton staffHours={!!staffHours && !parents} tz={tz} />}
          {startFamily && !parents && <NewFamily siteId={startFamily} onOpen={open} />}
          {canStaff && <NewStaff orgs={data.staff_orgs} onOpen={open} />}
        </div>
      )}
      {compact && <div className="pmm-inbox__search"><TextField label="Search conversations" hideLabel type="search" placeholder="Search names or latest messages" value={query} onChange={(e) => setQuery(e.target.value)} /><div className="pmm-inbox__filters" role="group" aria-label="Conversation filter"><Button size="sm" variant={onlyUnread ? "ghost" : "secondary"} aria-pressed={!onlyUnread} onClick={() => setOnlyUnread(false)}>All conversations</Button><Button size="sm" variant={onlyUnread ? "secondary" : "ghost"} aria-pressed={onlyUnread} onClick={() => setOnlyUnread(true)}>Unread</Button></div></div>}
      {/* The parent app: other modules' cards first (Lost and found). */}
      {parents && slot("guardian.inbox").map((e) => <Fragment key={e.id}>{e.render()}</Fragment>)}
      <Threads {...(compact ? { className: "pmm-inbox__threads" } : {})}>
      {!threads.length && <EmptyState icon="message" title="No messages yet" description={startFamily || canStaff ? "Start one with the buttons above." : "When your school sends you a message, it appears here."} />}
      {threads.length > 0 && !shown.length && <EmptyState icon="search" title={search ? "No conversations found" : "No unread conversations"} description={search ? "Try another name or message." : "You have read all the conversations in this inbox."} />}
      {SECTIONS.map((s) => {
        const items = shown.filter((t) => s.kinds.includes(t.kind));
        if (!items.length) return null;
        return (
          <section key={s.id} aria-labelledby={`pmm-${s.id}`}>
            <h2 id={`pmm-${s.id}`} className="pmm-inbox__head">{s.title(isParent)}</h2>
            <ul className="pmm-list">{items.map(row)}</ul>
          </section>
        );
      })}
      </Threads>
    </div>
  );
}

/** Settings: the language to read messages in, and a teacher's office hours with the away reply. `icon`: the top bar's button. */
export function PrefsButton({ staffHours, tz, icon }: { staffHours: boolean; tz?: string; icon?: boolean }) {
  const [p, reload] = usePrefs();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ lang: "", quiet_start: "", quiet_end: "", away_text: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!p) return null;
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api("PUT", "/api/v1/messages/prefs", { lang: f.lang || null, quiet_start: f.quiet_start || null, quiet_end: f.quiet_end || null, tz: tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone, away_text: f.away_text || null });
      reload(); setOpen(false);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <Dialog title="Message settings" open={open} onOpenChange={(v) => { setOpen(v); if (v) setF({ lang: p.lang ?? "", quiet_start: p.quiet_start ?? "", quiet_end: p.quiet_end ?? "", away_text: p.away_text ?? "" }); }}
      trigger={icon ? <IconButton icon="settings" label={p.quiet_now ? "Message settings (away now)" : "Message settings"} /> : <Button size="sm" variant="ghost" icon="settings">{p.quiet_now ? "Away now" : "Settings"}</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} onClick={() => void save()}>Save</Button></>}>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        {p.translate.available && (
          <Select label="Read messages in" hint="Messages from others are translated for you. The original is one tap away." value={f.lang || "none"}
            onValueChange={(v) => setF({ ...f, lang: !v || v === "none" ? "" : v })}
            options={[{ value: "none", label: "The language they were written in" }, ...p.translate.languages.map((l) => ({ value: l.code, label: l.name }))]} />
        )}
        {staffHours && (
          <>
            <p className="pmu-small pmu-muted">Office hours: outside them you get no pushes, just one when they start again, and parents who write about a child get an away reply once.</p>
            <div className="pmu-row">
              <TextField label="Away from" type="time" value={f.quiet_start} onChange={(e) => setF({ ...f, quiet_start: e.target.value })} />
              <TextField label="Back at" type="time" value={f.quiet_end} onChange={(e) => setF({ ...f, quiet_end: e.target.value })} />
            </div>
            <TextField label="Away reply (optional)" maxLength={300} value={f.away_text} onChange={(e) => setF({ ...f, away_text: e.target.value })} placeholder="I answer messages before school starts." />
          </>
        )}
        <BlockedPeople />
        <a className="pmm-link" href="https://vavr.us/terms" target="_blank" rel="noopener noreferrer">Community rules and terms</a>
      </div>
    </Dialog>
  );
}

/** Starting a family's conversation: a child from the teacher's rooms (or any child, for the office). */
function NewFamily({ siteId, onOpen }: { siteId: string; onOpen: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState<{ id: string; first: string; last: string; room_name: string | null }[] | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open && !kids) api<{ children: NonNullable<typeof kids> }>("GET", `/api/v1/sites/${siteId}/messages/children`).then((r) => setKids(r.children), (e: Error) => setError(e.message)); }, [open, kids, siteId]);
  const shown = (kids ?? []).filter((k) => `${k.first} ${k.last} ${k.room_name ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 50);
  const pick = async (id: string) => {
    try { const r = await api<{ id: string }>("POST", "/api/v1/messages/family", { site_id: siteId, child_id: id }); setOpen(false); void refreshInbox(); onOpen(r.id); }
    catch (e) { setError((e as Error).message); }
  };
  return (
    <Dialog title="Message a family" description="Their parents and the office see it." open={open} onOpenChange={(v) => { setOpen(v); setQ(""); }}
      trigger={<Button size="sm" variant="secondary" icon="child">Message a family</Button>} actions={<DialogClose><Button variant="secondary">Cancel</Button></DialogClose>}>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        <TextField label="Find a child" value={q} onChange={(e) => setQ(e.target.value)} placeholder="First letters of a name or class" />
        {!kids ? <Spinner label="Loading" /> : !shown.length ? <p className="pmu-muted">{kids.length ? "No child matches." : "No children in your classes."}</p> : (
          <ul className="pmm-list pmm-pick">
            {shown.map((k) => (
              <li key={k.id}><button type="button" className="pmm-row" onClick={() => void pick(k.id)}>
                <Avatar name={`${k.first} ${k.last}`} kind="child" size="sm" />
                <span className="pmm-row__main"><b className="pmm-row__title">{k.first} {k.last}</b><span className="pmm-row__sub">{k.room_name ?? "No class"}</span></span>
              </button></li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

/** A direct message to one colleague, or a named group. */
function NewStaff({ orgs, onOpen }: { orgs: { id: string; name: string }[]; onOpen: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState(orgs[0]!.id);
  const [people, setPeople] = useState<{ id: string; name: string; roles: string[] }[] | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setPeople(null); api<{ staff: NonNullable<typeof people> }>("GET", `/api/v1/messages/staff-directory?org=${encodeURIComponent(org)}`).then((r) => setPeople(r.staff), (e: Error) => setError(e.message)); } }, [open, org]);
  const group = chosen.size > 1;
  const ready = chosen.size > 0 && (!group || title.trim());
  const start = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    try { const r = await api<{ id: string }>("POST", "/api/v1/messages/staff", { org_id: org, account_ids: [...chosen], title: group ? title : undefined }); setOpen(false); void refreshInbox(); onOpen(r.id); }
    catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  const shown = (people ?? []).filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog title="New message" description="To one colleague, or a group you name." open={open} onOpenChange={(v) => { setOpen(v); if (v) { setChosen(new Set()); setTitle(""); setQ(""); } }}
      trigger={<Button size="sm" icon="plus">New message</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={busy} disabled={!ready} onClick={() => void start()}>{group ? "Start the group" : "Start"}</Button></>}>
      <form className="pmu-stack" onSubmit={(e) => void start(e)}>
        {error && <Banner tone="danger" title={error} />}
        {orgs.length > 1 && (
          <div className="pmu-row">{orgs.map((o) => <Button key={o.id} size="sm" variant={o.id === org ? "primary" : "secondary"} onClick={() => { setOrg(o.id); setChosen(new Set()); }}>{o.name}</Button>)}</div>
        )}
        <TextField label="Find a colleague" value={q} onChange={(e) => setQ(e.target.value)} />
        {!people ? <Spinner label="Loading" /> : !shown.length ? <p className="pmu-muted">{people.length ? "Nobody matches." : "No colleagues yet."}</p> : (
          <div className="pmm-people">
            {shown.map((p) => (
              <Checkbox key={p.id} label={p.name} checked={chosen.has(p.id)}
                onCheckedChange={(v) => setChosen((s) => { const n = new Set(s); if (v) n.add(p.id); else n.delete(p.id); return n; })} />
            ))}
          </div>
        )}
        {group && <TextField label="Group name" required value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="Afternoon dismissal" />}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

// ---------- a conversation ----------

const EMPTY: Partial<Record<Kind, (as: As) => string>> = {
  family: (as) => (PARENT_SIDE.has(as) ? "Write to your child's teachers. The office can read this conversation too." : "Write to this child's parents. The office can read this conversation too."),
  class: (as) => (PARENT_SIDE.has(as) ? "Your child's class news appears here." : "Write to every parent in this class."),
  school: (as) => (as === "office" ? "Write to every parent and all staff." : "The school's announcements appear here."),
  bus: (as) => (as === "parent" ? "Write to the bus company about your child's bus. The school office can read this too." : "Write to this child's parents."),
  partner: () => "The school office and the bus company's dispatch.",
};

export function ConversationView({ id, tz, onBack }: { id: string; tz?: string; onBack?: () => void }) {
  const [view, setView] = useState<ThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<{ id: string; name: string; type: string }[]>([]);
  const [outbox, setOutbox] = useState<{ id: string; body: string; created_at: number; files: number; queued?: boolean }[]>([]);
  // Offline (PMU-270): a text message waits on the device and is sent when the internet is back;
  // once the queue is empty again the thread reloads and shows it from the server.
  const off = useOffline();
  useEffect(() => {
    if (off.online && off.queued === 0 && outbox.some((o) => o.queued)) { setOutbox((o) => o.filter((x) => !x.queued)); void load(); }
  }, [off.online, off.queued]);
  const [emoji, setEmoji] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [prefs] = usePrefs();
  const [terms] = useTerms();
  const [translated, setTranslated] = useState<Record<string, string>>({});
  const [showOriginal, setShowOriginal] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const convo = useRef<HTMLDivElement>(null);
  // In the parent and staff apps the conversation's name and options go in the app's top bar.
  const inBar = !!useShellSlotsHere();
  const [people, setPeople] = useState(false);
  // The docked bar at the foot: the composer, or why this person cannot write here.
  const dock = useRef<HTMLElement | null>(null);
  const setDock = (el: HTMLElement | null) => { dock.current = el; };
  const load = useMemo(() => async () => {
    try {
      const r = await api<ThreadView>("GET", `/api/v1/messages/${id}`);
      // Keep older pages already loaded; the newest page replaces the tail.
      setView((v) => (v && v.thread.id === id ? { ...r, messages: merge(v.messages, r.messages), files: mergeFiles(v.files, r.files), more: v.more } : r));
      setError(null);
      void refreshInbox();
    } catch (e) { setError((e as Error).message); }
  }, [id]);
  useEffect(() => { setView(null); setTranslated({}); void load(); }, [load]);
  useEffect(() => {
    const off = onModuleLive((m: ModuleLiveMessage) => {
      if (m.module !== "messages" || m.thread_id !== id) return;
      // Someone received or read your messages: the ticks turn.
      if (m.type === "receipt") { void load(); return; }
      // A message with files needs their names: reload the page; otherwise add it as it came.
      void load();
      void api("POST", `/api/v1/messages/${id}/read`).then(() => refreshInbox(), () => undefined);
    });
    // Phones without the live socket (a bus crew's) still see replies within 20 s while the conversation is open.
    const t = setInterval(() => { if (document.visibilityState === "visible") void load(); }, 20_000);
    return () => { off(); clearInterval(t); };
  }, [id, load]);
  const count = view?.messages.length ?? 0;
  // A new message comes into view (above the chat bar), unless you have scrolled up to read older
  // ones; the first page always opens at the latest.
  const opened = useRef<string | null>(null);
  const lastId = view?.messages.at(-1)?.id;
  useEffect(() => {
    if (!lastId) return;
    const first = opened.current !== id;
    opened.current = id;
    const e = end.current;
    if (!e) return;
    const below = e.getBoundingClientRect().bottom - window.innerHeight;
    if (first || below < 600 || view?.messages.at(-1)?.author_id === view?.me) e.scrollIntoView({ block: "end" });
  }, [lastId, id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The keyboard opening shrinks the screen: keep the latest message in view, above the chat bar.
  useEffect(() => {
    // Also when it closes: the tab bar comes back a moment later, under the chat bar. Only if you were at the latest.
    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => { const e = end.current; if (e && e.getBoundingClientRect().bottom - window.innerHeight < 600) e.scrollIntoView({ block: "end" }); }, 200);
    };
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, []);
  /** The text box grows with what is typed, up to about six lines, then scrolls inside. */
  const grow = (el: HTMLTextAreaElement) => { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight + 2, 160)}px`; };
  useEffect(() => { if (!text) { const el = document.getElementById(`pmm-text-${id}`); if (el) el.style.height = ""; } }, [text, id]);
  // On a phone the bar is docked over the page: leave its height free under the last message.
  const docked = view ? `${view.thread.can_post}:${terms?.accepted ?? ""}` : "";
  useEffect(() => {
    const d = dock.current, c = convo.current;
    if (!d || !c || typeof ResizeObserver === "undefined") return;
    // A taller chat bar (a longer message, files added) keeps the latest message in view above it.
    const set = () => {
      c.style.setProperty("--pmm-dock-h", `${d.offsetHeight + 12}px`);
      if (document.activeElement instanceof HTMLTextAreaElement) end.current?.scrollIntoView({ block: "end" });
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(d);
    return () => ro.disconnect();
  }, [docked]);
  // Translation: others' messages in the reader's language, once each (the server caches them too).
  const lang = prefs?.translate.available ? prefs.lang : null;
  useEffect(() => {
    if (!lang || !view) return;
    const want = view.messages.filter((m) => m.author_id !== view.me && m.author_as !== "system" && m.body && !(m.id in translated)).map((m) => m.id);
    if (!want.length) return;
    void api<{ translations: Record<string, string> }>("POST", `/api/v1/messages/${id}/translate`, { lang, ids: want }).then((r) => setTranslated((x) => ({ ...x, ...r.translations })), () => undefined);
  }, [lang, view, id, translated]);

  /**
   * Sends at once: the message shows straight away with a clock, and the box is ready for the next
   * one; when the school has it, it takes its place with one tick. If it fails the text comes back.
   */
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body && !pending.length) return;
    const files = pending;
    const temp = { id: `out-${Date.now()}-${Math.random()}`, body, created_at: Date.now(), files: files.length };
    setOutbox((o) => [...o, temp]);
    setText(""); setPending([]); setError(null);
    // Keep typing: the box keeps the focus (and the keyboard stays up).
    document.getElementById(`pmm-text-${id}`)?.focus();
    setSending(true);
    try {
      // Text only can wait offline: files need the internet to upload first.
      const r = await api<{ message?: Message; queued?: boolean }>("POST", `/api/v1/messages/${id}`, { body, file_ids: files.map((f) => f.id) },
        files.length ? {} : { queue: { label: `Message "${body.slice(0, 40)}${body.length > 40 ? "…" : ""}"` } });
      if (r.queued) { setOutbox((o) => o.map((x) => (x.id === temp.id ? { ...x, queued: true } : x))); return; }
      setView((v) => (v ? { ...v, messages: merge(v.messages, [r.message!]) } : v));
      setOutbox((o) => o.filter((x) => x.id !== temp.id));
      void load();
    } catch (err) {
      setOutbox((o) => o.filter((x) => x.id !== temp.id));
      setText((t) => t || body); setPending((p) => (p.length ? p : files));
      setError((err as Error).message);
    } finally { setSending(false); }
  };
  /** A photo that loads after the scroll makes the message taller: keep the latest in view, if you were at it. */
  const keepAtEnd = () => { const e = end.current; if (e && e.getBoundingClientRect().bottom - window.innerHeight < 700) e.scrollIntoView({ block: "end" }); };
  /** Puts an emoji where the cursor is (or at the end), and keeps the cursor after it. */
  const insertEmoji = (e: string) => {
    const el = document.getElementById(`pmm-text-${id}`) as HTMLTextAreaElement | null;
    const at = el && document.activeElement === el ? el.selectionStart : text.length;
    const end = el && document.activeElement === el ? el.selectionEnd : text.length;
    const next = (text.slice(0, at) + e + text.slice(end)).slice(0, 2000);
    setText(next);
    requestAnimationFrame(() => { if (el) { el.setSelectionRange(at + e.length, at + e.length); grow(el); } });
  };
  const attach = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true); setError(null);
    try {
      for (const f of [...files].slice(0, 10 - pending.length)) {
        const r = await api<{ file: { id: string; name: string; type: string } }>("POST", `/api/v1/messages/${id}/files?name=${encodeURIComponent(f.name)}`, undefined, { raw: f, type: f.type });
        setPending((p) => [...p, r.file]);
      }
    } catch (err) { setError((err as Error).message); } finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };
  const older = async () => {
    if (!view?.messages.length) return;
    const r = await api<ThreadView>("GET", `/api/v1/messages/${id}?before=${view.messages[0]!.created_at}`);
    setView((v) => (v ? { ...v, messages: merge(r.messages, v.messages), files: mergeFiles(r.files, v.files), more: r.more } : v));
  };
  const setReplies = async (on: boolean) => {
    try { await api("PUT", `/api/v1/messages/${id}/settings`, { replies: on }); await load(); } catch (err) { setError((err as Error).message); }
  };
  const exportIt = async () => {
    try {
      const blob = await blobData(`/api/v1/messages/${id}/export`);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `conversation-${id}.txt`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (err) { setError((err as Error).message); }
  };
  const review = async (m: Message) => {
    try { await api("POST", `/api/v1/sites/${view!.thread.site_id}/messages/safeguarding/${m.id}`); await load(); } catch (err) { setError((err as Error).message); }
  };

  if (!view) return error ? <Banner tone="danger" title={error} actions={<Button size="sm" onClick={() => void load()}>Try again</Button>} /> : <div className="pmu-center"><Spinner label="Loading the conversation" /></div>;
  const { thread, me, messages, files } = view;
  // "Seen by" belongs under your own message: only when the latest one is yours.
  const lastMine = messages.at(-1)?.author_id === me ? messages.at(-1) : undefined;
  const seenBy = lastMine && view.receipts.people ? view.receipts.people.filter((r) => r.read_at >= lastMine.created_at).map((r) => r.name) : [];
  const last = messages.at(-1);
  const parentsSeen = view.receipts.parents && last ? view.receipts.parents.read_at.filter((x) => x >= last.created_at).length : null;
  const anyTranslated = messages.some((m) => translated[m.id] && translated[m.id] !== m.body);
  const langName = prefs?.translate.languages.find((l) => l.code === lang)?.name;
  return (
    <div className="pmm-convo" ref={convo}>
      {inBar ? (
        <ShellHeader title={thread.title} subtitle={thread.subtitle} actions={(() => {
          // The options in one menu (⋯); the family's people in a dialog opened from it.
          const items: MenuItem[] = [
            ...(thread.can_add_family && thread.site_id && thread.child_id ? [{ id: "people", label: "People in this conversation", icon: "user" as const, onSelect: () => setPeople(true) }] : []),
            ...(anyTranslated ? [{ id: "lang", label: showOriginal ? `Show in ${langName ?? "your language"}` : "Show original", icon: "message" as const, onSelect: () => setShowOriginal((x) => !x) }] : []),
            ...(thread.can_settings ? [{ id: "replies", label: thread.replies ? "Make it announcements only" : "Let parents reply to everyone", icon: "settings" as const, onSelect: () => void setReplies(!thread.replies) }] : []),
            ...(thread.can_export ? [{ id: "export", label: "Export the conversation", icon: "upload" as const, onSelect: () => void exportIt() }] : []),
          ];
          return items.length ? <Menu trigger={<IconButton icon="more" label="Conversation options" />} groups={[items]} /> : null;
        })()} />
      ) : (
        <>
          <header className="pmm-convo__head">
            {onBack && <Button size="sm" variant="ghost" icon="chevron-left" onClick={onBack}>Messages</Button>}
            <div className="pmm-convo__who">
              <b>{thread.title}</b>
              <span className="pmu-small pmu-muted">{thread.subtitle}</span>
            </div>
            <div className="pmm-convo__tools">
              {anyTranslated && <Button size="sm" variant="ghost" onClick={() => setShowOriginal((x) => !x)}>{showOriginal ? `Show in ${langName ?? "your language"}` : "Show original"}</Button>}
              {thread.can_add_family && thread.site_id && thread.child_id && <FamilyPeople siteId={thread.site_id} childId={thread.child_id} />}
              {thread.can_export && <Button size="sm" variant="ghost" icon="upload" onClick={() => void exportIt()}>Export</Button>}
            </div>
          </header>
          {thread.can_settings && (
            <Switch label="Parents can reply to everyone" description={thread.replies ? "A conversation among the class's parents and the school." : "Announcements only: parents read, and write to you in their child's conversation."}
              checked={thread.replies} onCheckedChange={(v) => void setReplies(v)} />
          )}
        </>
      )}
      {inBar && thread.can_add_family && thread.site_id && thread.child_id && <FamilyPeople siteId={thread.site_id} childId={thread.child_id} open={people} onOpenChange={setPeople} />}
      <div className="pmm-convo__list" role="log" aria-live="polite" aria-label={`Messages in ${thread.title}`}>
        {view.more && <Button size="sm" variant="ghost" onClick={() => void older()}>Earlier messages</Button>}
        {!messages.length && <p className="pmm-convo__empty">{EMPTY[thread.kind]?.(thread.as) ?? "Say hello."}</p>}
        {messages.map((m, i) => {
          const mine = m.author_id === me;
          const prev = messages[i - 1];
          const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
          const day = newDay && <p className="pmm-day">{new Intl.DateTimeFormat("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: tz }).format(m.created_at)}</p>;
          if (m.author_as === "system") return <div key={m.id} className="pmm-msgwrap">{day}<p className="pmm-system">{m.body}</p></div>;
          if (m.blocked) return <div key={m.id} className="pmm-msgwrap">{day}<p className="pmm-system">A message from someone you blocked</p></div>;
          if (m.hidden && !thread.can_moderate) return <div key={m.id} className="pmm-msgwrap">{day}<p className="pmm-system">Message removed by the school</p></div>;
          const sameAuthor = prev && !newDay && prev.author_id === m.author_id && prev.author_as !== "system" && m.created_at - prev.created_at < 5 * 60_000;
          const tr = !showOriginal && translated[m.id] && translated[m.id] !== m.body ? translated[m.id] : null;
          const mf = files.filter((f) => f.message_id === m.id);
          const role = thread.kind !== "staff" ? ROLE[m.author_as] : undefined;
          return (
            <div key={m.id} className="pmm-msgwrap">
              {day}
              <div className="pmm-msg" data-mine={mine || undefined} data-cont={sameAuthor || undefined} data-request={m.request_kind ? true : undefined}>
                {!mine && !sameAuthor && <span className="pmm-msg__by">{m.author_name}{role ? <Badge tone={PARENT_SIDE.has(m.author_as) ? "neutral" : "info"}>{role}</Badge> : null}</span>}
                {!mine && <MessageMenu thread={thread} m={m} onChanged={() => void load()} />}
                {m.hidden && <span className="pmm-msg__tag">Hidden from everyone</span>}
                {m.request_kind && <span className="pmm-msg__tag">Sent with Tell the school</span>}
                {m.body && <p className="pmm-msg__body">{tr ?? m.body}</p>}
                {mf.map((f) => <Attachment key={f.id} thread={id} file={f} onLoad={keepAtEnd} />)}
                <span className="pmm-msg__at">{tr ? "Translated · " : ""}{new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(m.created_at)}{mine && <Ticks at={m.created_at} ticks={view.ticks} />}</span>
                {m.flagged && (
                  <span className="pmm-msg__flag">
                    <Badge tone={m.reviewed ? "neutral" : "warning"}>{m.reviewed ? "Reviewed" : "Flagged"}: {m.flagged}</Badge>
                    {!m.reviewed && <Button size="sm" variant="ghost" onClick={() => void review(m)}>Mark reviewed</Button>}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {outbox.map((o) => (
          <div key={o.id} className="pmm-msgwrap">
            <div className="pmm-msg" data-mine data-sending>
              {o.body && <p className="pmm-msg__body">{o.body}</p>}
              {o.files > 0 && <p className="pmm-msg__body pmu-small">{o.files === 1 ? "1 file" : `${o.files} files`}</p>}
              <span className="pmm-msg__at">{o.queued ? "Waiting for the internet" : "Sending"} <Icon name="clock" label={o.queued ? "Waiting for the internet" : "Sending"} className="pmm-tick" /></span>
            </div>
          </div>
        ))}
        {seenBy.length > 0 && !outbox.length && <p className="pmm-seen">Seen by {seenBy.join(", ")}</p>}
        {parentsSeen != null && view.receipts.parents!.total > 0 && <p className="pmm-seen">Seen by {parentsSeen} of {view.receipts.parents!.total} {view.receipts.parents!.total === 1 ? "parent" : "parents"}</p>}
        <div ref={end} className="pmm-end" />
      </div>
      {error && <Banner tone="danger" title={error} />}
      {/* Read only, or the rules still to accept: in the same docked bar as the composer, on top of the bottom menu. */}
      {!thread.can_post ? <div className="pmm-compose pmm-compose--note" ref={setDock}><p className="pmm-readonly"><span className="pmm-readonly__icon" aria-hidden="true"><Icon name="lock" /></span><span>{thread.read_only_why ?? "You can read this conversation."}</span></p></div>
        : terms && !terms.accepted ? <div className="pmm-compose pmm-compose--note" ref={setDock}><RulesCard compact /></div> : (
        <form className="pmm-compose" ref={setDock} onSubmit={(e) => void send(e)}>
          {pending.length > 0 && (
            <ul className="pmm-pending" aria-label="Files to send">
              {pending.map((f) => <li key={f.id}><Icon name={f.type.startsWith("image/") ? "camera" : "note"} /> {f.name}
                <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setPending((p) => p.filter((x) => x.id !== f.id))}><Icon name="close" /></button></li>)}
            </ul>
          )}
          {emoji && <EmojiPanel onPick={(e) => insertEmoji(e)} />}
          <div className="pmm-compose__row">
            <input ref={fileInput} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={(e) => void attach(e.target.files)} />
            <button type="button" className="pmm-compose__attach" onMouseDown={(e) => e.preventDefault()} aria-label="Add a photo or file" disabled={uploading || pending.length >= 10} onClick={() => fileInput.current?.click()}>
              {uploading ? <Spinner label="Uploading" /> : <Icon name="plus" />}
            </button>
            <button type="button" className="pmm-compose__attach" onMouseDown={(e) => e.preventDefault()} aria-label={emoji ? "Close emoji" : "Add an emoji"} aria-expanded={emoji} onClick={() => setEmoji((x) => !x)}>
              <Icon name={emoji ? "close" : "smile"} />
            </button>
            <label className="pmu-sr-only" htmlFor={`pmm-text-${id}`}>Message</label>
            <textarea id={`pmm-text-${id}`} className="pmm-compose__text" rows={1} maxLength={2000} value={text} placeholder="Write a message"
              onChange={(e) => { setText(e.target.value); grow(e.target); }}
              // The keyboard shrinks the screen: keep the latest message in view above it.
              onFocus={() => setTimeout(() => end.current?.scrollIntoView({ block: "end" }), 350)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !("ontouchstart" in window)) { e.preventDefault(); void send(); } }} />
            {/* Tapping Send (or +) must not take the focus from the text box, or the keyboard closes. */}
            <button type="submit" className="pmm-compose__send" onMouseDown={(e) => e.preventDefault()} disabled={(!text.trim() && !pending.length) || uploading} aria-label="Send"><Icon name="send" /></button>
          </div>
        </form>
      )}
    </div>
  );
}

/** A photo shows in the message; another file is a link that downloads it. */
function Attachment({ thread, file, onLoad }: { thread: string; file: MsgFile; onLoad?: () => void }) {
  const path = `/api/v1/messages/${thread}/files/${file.id}`;
  const img = file.type.startsWith("image/");
  const url = useBlobUrl(img ? path : null);
  const download = async () => {
    const blob = await blobData(path);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };
  if (img) return url ? <img className="pmm-photo" src={url} alt={file.name} onLoad={onLoad} /> : <span className="pmm-photo pmm-photo--loading" aria-label={`Loading ${file.name}`} />;
  return <button type="button" className="pmm-file" onClick={() => void download()}><Icon name="note" /> {file.name} <span className="pmu-muted">· {Math.ceil(file.size / 1024)} KB</span></button>;
}

/** A parent brings another adult on the child's list (who has the app) into the child's conversation. */
function FamilyPeople({ siteId, childId, open: shown, onOpenChange }: { siteId: string; childId: string; open?: boolean; onOpenChange?: (o: boolean) => void }) {
  // Opened from its own button, or (`open`) from the conversation's options menu.
  const [own, setOwn] = useState(false);
  const controlled = shown !== undefined;
  const open = controlled ? shown : own;
  const setOpen = (o: boolean) => (controlled ? onOpenChange?.(o) : setOwn(o));
  const [adults, setAdults] = useState<{ id: string; name: string; relation: string; has_app: boolean; in_conversation: boolean }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/v1/sites/${siteId}/messages/children/${childId}/adults`;
  const load = () => api<{ adults: NonNullable<typeof adults> }>("GET", base).then((r) => setAdults(r.adults), (e: Error) => setError(e.message));
  useEffect(() => { if (open) void load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = async (a: { id: string }, on: boolean) => { try { await api("PUT", `${base}/${a.id}`, { in_conversation: on }); await load(); } catch (e) { setError((e as Error).message); } };
  return (
    <Dialog title="Who else is in this conversation" description="Other adults on your child's list who have the Vavr app. They read and write here like you." open={open} onOpenChange={setOpen}
      trigger={controlled ? undefined : <Button size="sm" variant="ghost" icon="user">People</Button>} actions={<DialogClose><Button variant="secondary">Done</Button></DialogClose>}>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        {!adults ? <Spinner label="Loading" /> : !adults.length ? <p className="pmu-muted">No other adults are on your child's list. Add them under People first.</p> : adults.map((a) => (
          <Switch key={a.id} label={`${a.name} (${a.relation})`} description={a.has_app ? undefined : "Not on the Vavr app yet, so they cannot read messages."}
            checked={a.in_conversation} disabled={!a.has_app && !a.in_conversation} onCheckedChange={(v) => void set(a, v)} />
        ))}
      </div>
    </Dialog>
  );
}

// ---------- the console's safeguarding page ----------

interface Flagged { id: string; thread_id: string; author_name: string; author_as: As; body: string; created_at: number; flagged: string; reviewed_at: number | null; reviewed_by_name: string | null; kind: Kind }

/** The office: messages the school's words flagged, and the words. */
export function SafeguardingView({ siteId, tz, open }: { siteId: string; tz?: string; open: (id: string) => void }) {
  const base = `/api/v1/sites/${siteId}/messages/safeguarding`;
  const [data, setData] = useState<{ words: string[]; custom: boolean; defaults: string[]; flagged: Flagged[] } | null>(null);
  const [words, setWords] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const load = useMemo(() => () => api<NonNullable<typeof data>>("GET", base).then((r) => { setData(r); setWords(r.words.join(", ")); }, (e: Error) => setError(e.message)), [base]);
  useEffect(() => { void load(); }, [load]);
  if (!data) return error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading" />;
  const save = async () => {
    setBusy(true); setError(null); setSaved(false);
    try { await api("PUT", base, { words: words.split(",").map((w) => w.trim()).filter(Boolean) }); await load(); setSaved(true); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const at = (ts: number) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz }).format(ts);
  return (
    <div className="pmu-stack">
      {error && <Banner tone="danger" title={error} />}
      <section className="pmu-stack">
        <h2 className="pmm-inbox__head">Flagged messages</h2>
        {!data.flagged.length ? <p className="pmu-muted">Nothing flagged. A message in a family's, a class's or a bus conversation that contains one of the words below is flagged here, and the office is emailed.</p> : (
          <ul className="pmm-list">
            {data.flagged.map((f) => (
              <li key={f.id}><button type="button" className="pmm-row" onClick={() => open(f.thread_id)}>
                <span className="pmm-row__main">
                  <span className="pmm-row__top"><b className="pmm-row__title">{f.author_name}</b><span className="pmm-row__when">{at(f.created_at)}</span></span>
                  <span className="pmm-row__sub">{f.body}</span>
                </span>
                <Badge tone={f.reviewed_at ? "neutral" : "warning"}>{f.reviewed_at ? `Reviewed by ${f.reviewed_by_name ?? "the office"}` : f.flagged}</Badge>
              </button></li>
            ))}
          </ul>
        )}
      </section>
      <section className="pmu-stack">
        <h2 className="pmm-inbox__head">Words that flag a message</h2>
        <TextField label="Words and phrases, separated by commas" value={words} onChange={(e) => { setWords(e.target.value); setSaved(false); }}
          hint={data.custom ? "Your school's list." : "Vavr's starting list until you save your own."} />
        <div className="pmu-row"><Button loading={busy} onClick={() => void save()}>Save the words</Button>{saved && <span className="pmu-small pmu-muted">Saved.</span>}</div>
      </section>
    </div>
  );
}

/** Messages in order, without duplicates (a live copy and the page can both carry one). */
function merge(a: Message[], b: Message[]) {
  const byId = new Map<string, Message>();
  for (const m of [...a, ...b]) byId.set(m.id, m);
  return [...byId.values()].sort((x, y) => x.created_at - y.created_at);
}
function mergeFiles(a: MsgFile[], b: MsgFile[]) { return [...new Map([...a, ...b].map((f) => [f.id, f])).values()]; }

/** One tick: sent (the school has it). Two: someone else's app has it. Two in colour: someone read it. */
function Ticks({ at, ticks }: { at: number; ticks?: { delivered_at: number; read_at: number } }) {
  const read = !!ticks && ticks.read_at >= at, got = !!ticks && ticks.delivered_at >= at;
  return <Icon name={got || read ? "check-double" : "check"} label={read ? "Read" : got ? "Received" : "Sent"} className={`pmm-tick${read ? " pmm-tick--read" : ""}`} />;
}

// The emoji a school conversation needs, drawn by the phone's own font: no library, nothing to download.
const EMOJI: { label: string; list: string }[] = [
  { label: "Smileys", list: "😀 😃 😄 😁 😊 🙂 😉 😍 🥰 😘 😋 😎 🤗 🤔 😐 😴 😮 😢 😭 😅 😂 🤣 😬 🙄 😷 🤒 🤕 🥳 😇 🙏" },
  { label: "Hands", list: "👍 👎 👏 🙌 👋 🤝 👌 ✌️ 🤞 💪 ✋ 👉 👆 🙋 🤷" },
  { label: "Hearts", list: "❤️ 🧡 💛 💚 💙 💜 🤍 💖 💕 ⭐ ✨ 🎉 🎊 🔥 💯" },
  { label: "School", list: "🏫 🎒 📚 📖 ✏️ 🖍️ 📝 🎨 🧮 🔬 🎵 ⚽ 🏀 🍎 🥪 🧃 🍪 🚌 🚗 🚶 🕒 📅 🌧️ ☀️ 🤧 🩹 💊 🏥 🎂 🎁" },
];

function EmojiPanel({ onPick }: { onPick: (e: string) => void }) {
  return (
    <div className="pmm-emoji" role="group" aria-label="Emoji">
      {EMOJI.map((g) => (
        <div key={g.label} className="pmm-emoji__group">
          <p className="pmm-emoji__label">{g.label}</p>
          <div className="pmm-emoji__grid">
            {g.list.split(" ").map((e) => (
              // Tapping one must not take the focus from the text box (the keyboard would close).
              <button key={e} type="button" className="pmm-emoji__btn" onMouseDown={(x) => x.preventDefault()} onClick={() => onPick(e)} aria-label={e}>{e}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A message's actions (PMU-237): report it; block its writer (a parent cannot block the school's
 * staff, only other parents; staff can block colleagues); a moderator hides it for everyone, or
 * shows it again.
 */
function MessageMenu({ thread, m, onChanged }: { thread: ThreadView["thread"]; m: Message; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const staffAuthor = !PARENT_SIDE.has(m.author_as);
  const canBlock = !staffAuthor || !!thread.me_staff;
  const act = async (key: string, fn: () => Promise<unknown>) => { setBusy(key); setError(null); try { await fn(); setOpen(false); onChanged(); } catch (e) { setError((e as Error).message); } finally { setBusy(null); } };
  return (
    <Dialog title={`Message from ${m.author_name}`} open={open} onOpenChange={setOpen}
      trigger={<button type="button" className="pmm-msg__more" aria-label={`More for the message from ${m.author_name}`} onMouseDown={(e) => e.preventDefault()}><Icon name="menu" /></button>}
      actions={<DialogClose><Button variant="secondary">Close</Button></DialogClose>}>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        <ReportDialog path={`/api/v1/messages/${thread.id}/report`} extra={{ message_id: m.id }} what="this message" trigger={<Button variant="secondary" icon="warning" fullWidth>Report this message</Button>} />
        {canBlock ? <Button variant="secondary" fullWidth loading={busy === "block"} onClick={() => void act("block", () => api("POST", "/api/v1/safety/blocks", { account_id: m.author_id }))}>Block {m.author_name}</Button>
          : <p className="pmu-small pmu-muted">You can't block your child's school. Report the message and the school's leaders will look at it.</p>}
        {thread.can_moderate && <Button variant="secondary" fullWidth loading={busy === "hide"} onClick={() => void act("hide", () => api("POST", `/api/v1/messages/${thread.id}/hide`, { message_id: m.id, show: !!m.hidden }))}>{m.hidden ? "Show it again" : "Hide it from everyone"}</Button>}
      </div>
    </Dialog>
  );
}

/** People this person blocked, to unblock (in Message settings). */
function BlockedPeople() {
  const [list, setList] = useState<{ id: string; name: string }[] | null>(null);
  const load = () => api<{ blocks: { id: string; name: string }[] }>("GET", "/api/v1/safety/blocks").then((r) => setList(r.blocks), () => setList([]));
  useEffect(() => { void load(); }, []);
  if (!list?.length) return null;
  return (
    <div className="pmu-stack">
      <b>Blocked</b>
      {list.map((b) => <div key={b.id} className="pmu-row pmu-row--between"><span>{b.name}</span><Button size="sm" variant="ghost" onClick={async () => { await api("DELETE", `/api/v1/safety/blocks/${b.id}`); await load(); }}>Unblock</Button></div>)}
    </div>
  );
}
